import React, { Component, ReactNode } from 'react';
import i18n from 'i18next';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** When this key changes the error state is cleared without a full remount. */
  resetKey?: string | number;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (
      this.props.resetKey !== undefined &&
      this.props.resetKey !== prevProps.resetKey &&
      this.state.hasError
    ) {
      this.setState({ hasError: false, error: undefined });
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error fallback UI
      return (
        <div className="my-4 overflow-hidden rounded-xl bg-red-50 p-6 shadow-sm ring-1 ring-red-200 transition-all duration-200 dark:bg-red-950">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">
                {i18n.t('componentError', { ns: 'pleri/chat' })}
              </h3>
              <p className="mt-1 text-sm text-red-600">
                {i18n.t('somethingWentWrongWhileRenderingThisComponent', {
                  ns: 'pleri/chat',
                })}
              </p>
              {this.state.error && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-red-500 hover:text-red-700">
                    {i18n.t('showErrorDetails', { ns: 'pleri/chat' })}
                  </summary>
                  <pre className="mt-1 text-xs text-red-600">
                    {this.state.error.message}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
